import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction, UserMin } from '@apiContract';
import AssignTO from '@components/DrawerContent/AssignmentList/components/AssignTo';
import SelectUsers from '@components/DrawerContent/SelectUsers';
import { SingleSelectReturn } from '@components/DrawerContent/SelectUsers/types';

import { ActionComponentProps } from '../../index';

export const Forward = ({ mailpieces, actionStarted, onActionFinish, isForce }: ActionComponentProps) => {
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const [updatePieces] = useUpdatePiecesMutation();

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [recipient, setRecipient] = useState<UserMin | null>(null);
  const [comment, setComment] = useState<string>('');
  const [notifyMailroom, setNotifyMailroom] = useState<boolean>(false);
  const [selectDrawer, setSelectDrawer] = useState<boolean>(false);

  const pieceQuantity = mailpieces.length;

  const handleChangeRecipient = (user: UserMin | null) => {
    setRecipient(user);
  };

  const handleChangeComment = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const reset = () => {
    setRecipient(null);
    setComment('');
    setNotifyMailroom(false);
  };

  useEffect(() => {
    if (actionStarted) {
      reset();
    }
  }, [actionStarted]);

  const handleSubmit = async () => {
    if (!recipient) return;

    try {
      setActionInProgress(true);

      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id, recipientId: recipient.id })),
        action: PieceAction.RequestForward,
        isForce,
        ...(isForce && { shouldNotify: notifyMailroom }),
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom: notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      setActionInProgress(false);
      onActionFinish();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Dialog slotProps={{ paper: { sx: { width: 600 } } }} open={actionStarted} sx={{ zIndex: '1200' }}>
        <DialogTitle>{pieceQuantity > 1 ? 'Forward selected items' : 'Forward item'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {pieceQuantity > 1 ? (
              <>
                To forward <strong>{pieceQuantity}</strong> items and their contents to someone else, select a
                recipient.
              </>
            ) : (
              'To forward this item and its contents to someone else, select a recipient.'
            )}
          </Typography>

          <AssignTO
            user={recipient}
            unassignedText="Select recipient"
            style={{ padding: 0 }}
            onClick={() => setSelectDrawer(true)}
          />

          <TextField
            sx={{ width: '100%', marginY: '24px' }}
            label="Optional Comment"
            multiline
            rows={3}
            value={comment}
            onChange={handleChangeComment}
          />

          <FormControlLabel
            label="Notify Mailroom"
            control={<Switch />}
            checked={notifyMailroom}
            onChange={() => setNotifyMailroom(!notifyMailroom)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onActionFinish} color="inherit" disabled={actionInProgress}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={actionInProgress}
            startIcon={actionInProgress && <SaveIcon />}
            loadingPosition={actionInProgress ? 'start' : undefined}
            variant="contained"
            color="primary"
            disabled={!recipient}
          >
            Forward
          </Button>
        </DialogActions>
      </Dialog>
      <SelectUsers
        filterOwner
        isOpened={selectDrawer}
        selected={recipient || undefined}
        onClose={() => setSelectDrawer(false)}
        onSave={(user) => {
          const data = user as SingleSelectReturn;
          handleChangeRecipient(data);
          setSelectDrawer(false);
        }}
        model="Recipient"
        isSingleSelect
      />
    </>
  );
};
